import { types, onSnapshot } from "mobx-state-tree";
import fetcher from "../utils/fetcher";
import { persist } from "../utils/persist";

const Role = {
  Admin: "Admin",
  Tenant: "Tenant",
};

const UserDetails = types.model("UserDetails", {
  USERNAME: types.maybeNull(types.string),
  NAME: types.maybeNull(types.string),
  USERID: types.optional(types.integer, 0),
  CUST: types.optional(types.integer, 0),
  CUSTNAME: types.optional(types.string, ""),
  CUSTDES: types.optional(types.string, ""),
  CELLPHONE: types.optional(types.string, ""),
  MAINCUST: types.optional(types.integer, 0),
  token: types.optional(types.string, ""),
  LOGO: types.optional(types.string, ""),
  MAINENV: types.optional(types.string, ""),
  MAINCUSTDES: types.optional(types.string, ""),
  PASSCHGREQFLAG: types.maybeNull(types.string, ""),
  ALL_TENANT_SPACES: types.frozen(),
  role: types.maybe(types.enumeration("Role", [Role.Admin, Role.Tenant])),
});

const UserStore = types
  .model("UserStore", {
    userDetails: UserDetails,
    allContracts: types.optional(types.boolean, false),
    error: types.frozen(),
    isLoading: types.optional(types.boolean, false),
    language: types.optional(types.string, "he"),
    isOtpVerify: types.optional(types.boolean, false),
    isAuthenticated: types.optional(types.boolean, false),
    parts: types.frozen(),
    applicationAttributes: types.frozen(),
  })

  .actions((self) => {
    const clearTenantSessionStorage = () => {
      // Remove both the correct key and the legacy typo key
      window.sessionStorage.removeItem("coralis-tenants-user");
      window.sessionStorage.removeItem("coralis-teanets-user");
      window.sessionStorage.removeItem("coralis-tenants-storerepairpage");
      window.sessionStorage.removeItem("coralis-tenants-storetenant");
      window.sessionStorage.removeItem("coralis-tenants-storeuser");
    };

    function logout() {
      self.setUserDetails({});
      self.setIsAuthenticated(false);
      self.setIsOtpVerify(false);

      clearTenantSessionStorage();
    }

    async function loginTenant(data) {
      self.setError("");
      self.setUserDetails({});
      self.setLoading(true);
      clearTenantSessionStorage();
      const dataBody = data;
      const response = await fetcher()
        .post(dataBody)
        .from.tenants.login_tenant_new();
      self.setLoading(false);

      if (response?.status !== 200) {
        const status = response?.status;
        const errorText =
          status === 404
            ? "error:error_failed_login_user_not_exists"
            : status === 401
              ? "error:error_failed_login_wrong_cardential"
              : status === 400
                ? "error:error_failed_user_error_login_req"
                : "error:error_failed_login_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      let userDetails = {
        ...data,
        ...response.data,
        USERID: response.data.ID,
        MAINCUST: response.data.CUST,
        role: Role.Tenant,
        MAINENV: response.data.DNAME,
      };



      self.setUserDetails(userDetails);
      window.sessionStorage.setItem(
        "coralis-tenants-user",
        JSON.stringify({
          userDetails: self.userDetails,
          language: self.language,
          isAuthenticated: self.isAuthenticated,
        }),
      );

      return {
        isSucceed: true,
        data: self.userDetails,
      };
    }

    async function verifyOTP(data) {
      self.setError("");
      const response = await fetcher().post(data).from.tenants.verify_OTP(data);

      if (response?.status === 200 && response?.data !== undefined) {
        return response.data;
      } else {
        const error =
          response.data.error_message || "error:error_failed_verify_opt_req";
        self.setError(error);
      }
    }

    async function sendOTP(data) {
      self.setError("");
      const response = await fetcher().post(data).from.tenants.send_OTP(data);

      if (response?.status === 200 && response?.data !== undefined) {
        return response?.data;
      } else {
        // Check for Twilio error 60203 (Max send attempts reached)
        if (
          response?.data?.error_code === 60203 ||
          response?.data?.error_message?.includes("Max send attempts")
        ) {
          self.setError("error:error_max_send_attempts_reached");
        } else {
          const error =
            response.data.error_message || "error:error_failed_send_opt_req";
          self.setError(error);
        }
      }
    }

    async function getEncryptedUser(data) {
      self.setError("");
      const encryptedRes = await fetcher()
        .post(data)
        .from.tenants.get_encrypted_user();
      if (encryptedRes?.status === 200 && encryptedRes?.data) {
        return encryptedRes.data.encrypted;
      } else {
        self.setError("error:error_failed_get_encrypted_user_req");
      }
    }

    async function loginAdmin(data) {
      self.setError("");
      self.setUserDetails({});
      self.setLoading(true);
      clearTenantSessionStorage();
      const dataBody = data;
      const response = await fetcher()
        .post(dataBody)
        .from.tenants.login_admin();
      self.setLoading(false);

      if (response?.status !== 200) {
        const status = response?.status;
        const errorText =
          status === 404
            ? "error:error_failed_login_user_not_exists"
            : status === 401
              ? "error:error_failed_login_wrong_cardential"
              : status === 400
                ? "error:error_failed_user_error_login_req"
                : "error:error_failed_login_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }

      let userDetails = {
        ...data,
        ...response.data,
        USERID: response.data.ID,
        MAINCUST: response.data.CUST,
        role: Role.Admin,
      };

      self.setUserDetails(userDetails);
      window.sessionStorage.setItem(
        "coralis-tenants-user",
        JSON.stringify({
          userDetails: self.userDetails,
          language: self.language,
          isAuthenticated: self.isAuthenticated,
        }),
      );

      return {
        isSucceed: true,
        data: self.userDetails,
      };
    }

    async function resetPassword(data) {
      self.setError("");
      const response = await fetcher().post(data).from.auth.reset_password();

      if (response?.status !== 200) {
        const errorText =
          response?.status === 404
            ? "error:error_failed_user_not_exists"
            : "error:error_failed_reset_password_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    const getApplicationAttributes = async (data) => {
      self.setError("");

      const appsRes = await fetcher()
        .post(data)
        .from.common.get_application_attributes();

      if (appsRes?.status === 200 && appsRes?.data) {
        const apps = appsRes.data.reduce((acc, app) => {
          const key = app["APPCODE"];
          if (!acc[key]) {
            acc[key] = {};
          }

          const attrebutesHash = app.ZCOK_APPATTRIBUTES_SUBFORM.reduce(
            (atributes, attribute) => {
              const key = attribute["ATTRIBUTECODE"];
              atributes[key] =
                attribute["ZCOK_USERAPPATTRIBUT_SUBFORM"]?.find(
                  (u) => u.USERNAME === userStore.userDetails.USERNAME,
                )?.MODE || attribute["MODE"];
              return atributes;
            },
            {},
          );

          acc[key] = attrebutesHash;

          return acc;
        }, {});

        self.setApplicationAttributes(apps);
      } else {
        self.setError(
          "error:error_failed_get_application_attributes_req",
          appsRes,
        );
      }
    };

    async function sendResetPassword(data) {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.auth.send_reset_password();
      if (response?.status !== 200) {
        var errorText = "error:error_failed_send_reset_req";
        if (response?.status === 404) {
          errorText = "error:error_failed_login_user_not_exists";
        }
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    function setIsOtpVerify(user) {
      self.isOtpVerify = user;
    }

    function setError(error) {
      self.error = error;
    }

    function setUserDetails(userDetailsData) {
      self.userDetails = { ...self.userDetails, ...userDetailsData };

      window.sessionStorage.setItem(
        "coralis-tenants-user",
        JSON.stringify({
          userDetails: self.userDetails,
          language: self.language,
          isAuthenticated: self.isAuthenticated,
        }),
      );
    }

    function setLoading(loading) {
      self.isLoading = loading;
    }

    function setIsAuthenticated(authenticated) {
      self.isAuthenticated = authenticated;
      window.sessionStorage.setItem(
        "coralis-tenants-user",
        JSON.stringify({
          isAuthenticated: self.isAuthenticated,
          language: self.language,
          userDetails: self.userDetails,
        }),
      );
    }

    function setLanguage(lang) {
      if (!lang || (lang !== "he" && lang !== "en")) {
        console.warn("Invalid language:", lang, "Defaulting to 'he'");
        lang = "he";
      }

      self.language = lang;

      try {
        window.localStorage.setItem("coralis-language-preference", lang);
        const saved = window.localStorage.getItem(
          "coralis-language-preference",
        );
        if (saved !== lang) {
          console.error(
            "Failed to save language to localStorage. Expected:",
            lang,
            "Got:",
            saved,
          );
        } else {
          console.log("Language saved to localStorage:", lang);
        }
      } catch (e) {
        console.error("Error saving language to localStorage:", e);
        try {
          window.localStorage.removeItem("coralis-language-preference");
          window.localStorage.setItem("coralis-language-preference", lang);
        } catch (e2) {
          console.error("Failed to save language even after cleanup:", e2);
        }
      }

      try {
        window.sessionStorage.setItem(
          "coralis-tenants-user",
          JSON.stringify({
            isAuthenticated: self.isAuthenticated,
            language: self.language,
            userDetails: self.userDetails,
          }),
        );
      } catch (e) {
        console.error("Error saving language to sessionStorage:", e);
      }
    }

    async function checkAdminPassword(data) {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.check_admin_password();
      if (response?.status !== 200) {
        const errorText = "error:error_failed_check_password_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          hasPassword: false,
          error: errorText,
        };
      }
      // API should return { hasPassword: true/false }
      const hasPassword = response?.data?.hasPassword === true;
      return {
        isSucceed: true,
        hasPassword: hasPassword,
      };
    }

    async function verifyAdminPassword(data) {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.tenants.verify_admin_password();
      if (response?.status !== 200) {
        const errorText =
          response?.status === 401
            ? "error:error_failed_login_wrong_cardential"
            : "error:error_failed_login_req";
        self.setError(errorText);
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      // Store user details from response
      if (response?.data) {
        let userDetails = {
          ...data,
          ...response.data,
          USERID: response.data.ID,
          MAINCUST: response.data.CUST,
          role: Role.Admin,
        };
        self.setUserDetails(userDetails);
        window.sessionStorage.setItem(
          "coralis-tenants-user",
          JSON.stringify({
            userDetails: self.userDetails,
            language: self.language,
            isAuthenticated: self.isAuthenticated,
          }),
        );
      }
      return {
        isSucceed: true,
      };
    }

    function setApplicationAttributes(applicationAttributes) {
      self.applicationAttributes = applicationAttributes;
    }

    return {
      loginTenant,
      loginAdmin,
      logout,
      setLoading,
      setUserDetails,
      setError,
      setLanguage,
      setIsAuthenticated,
      resetPassword,
      sendResetPassword,
      verifyOTP,
      sendOTP,
      setIsOtpVerify,
      getEncryptedUser,
      checkAdminPassword,
      verifyAdminPassword,
      getApplicationAttributes,
      setApplicationAttributes,
    };
  })
  .views((self) => ({
    get isError() {
      return !!self.error;
    },
    getUser() {
      return self.toJSON();
    },
    get vars() {
      return self.userDetails.toJSON();
    },
  }));

const sessionStorageUser = JSON.parse(
  window.sessionStorage.getItem("coralis-tenants-user") || "null",
);

const getInitialLanguage = () => {
  try {
    const localStorageLang = window.localStorage.getItem(
      "coralis-language-preference",
    );
    if (
      localStorageLang &&
      (localStorageLang === "he" || localStorageLang === "en")
    ) {
      console.log("Loading language from localStorage:", localStorageLang);
      return localStorageLang;
    }
  } catch (e) {
    console.error("Error reading from localStorage:", e);
  }

  if (
    sessionStorageUser?.language &&
    (sessionStorageUser.language === "he" ||
      sessionStorageUser.language === "en")
  ) {
    console.log(
      "Loading language from sessionStorage:",
      sessionStorageUser.language,
    );
    return sessionStorageUser.language;
  }

  console.log("Using default language: he");
  return "he";
};

const initialLanguage = getInitialLanguage();

const userStore = UserStore.create({
  userDetails: sessionStorageUser?.userDetails || {},
  error: "",
  isLoading: false,
  allContracts: false,
  isAuthenticated: sessionStorageUser?.isAuthenticated || false,
  language: initialLanguage,
  parts: [],
  isOtpVerify: false,
});

let snapshotListenerActive = false;
onSnapshot(userStore, (snapshot) => {
  if (!snapshotListenerActive) {
    return;
  }

  if (snapshot.language) {
    try {
      const currentSaved = window.localStorage.getItem(
        "coralis-language-preference",
      );
      if (currentSaved !== snapshot.language) {
        window.localStorage.setItem(
          "coralis-language-preference",
          snapshot.language,
        );
        console.log("Language auto-synced to localStorage:", snapshot.language);
      }
    } catch (e) {
      console.error(
        "Failed to save language to localStorage in onSnapshot:",
        e,
      );
    }
  }
});

setTimeout(() => {
  snapshotListenerActive = true;
  console.log(
    "Snapshot listener activated - language changes will now sync to localStorage",
  );
}, 200);

const persistData = window.sessionStorage.getItem("coralis-tenants-storeuser");
const localStorageLangForPersist = window.localStorage.getItem(
  "coralis-language-preference",
);

if (persistData) {
  try {
    const snapshot = JSON.parse(persistData);
    if (localStorageLangForPersist) {
      snapshot.language = localStorageLangForPersist;
      console.log(
        "Correcting persist snapshot language to:",
        localStorageLangForPersist,
      );
      window.sessionStorage.setItem(
        "coralis-tenants-storeuser",
        JSON.stringify(snapshot),
      );
    }
  } catch (e) {
    console.error("Error parsing persist data:", e);
    if (localStorageLangForPersist) {
      try {
        const newSnapshot = {
          userDetails: sessionStorageUser?.userDetails || {},
          error: "",
          isLoading: false,
          allContracts: false,
          isAuthenticated: sessionStorageUser?.isAuthenticated || false,
          language: localStorageLangForPersist,
          parts: [],
          isOtpVerify: false,
        };
        window.sessionStorage.setItem(
          "coralis-tenants-storeuser",
          JSON.stringify(newSnapshot),
        );
      } catch (e2) {
        console.error("Error creating new snapshot:", e2);
      }
    }
  }
} else if (localStorageLangForPersist) {
  try {
    const newSnapshot = {
      userDetails: sessionStorageUser?.userDetails || {},
      error: "",
      isLoading: false,
      allContracts: false,
      isAuthenticated: sessionStorageUser?.isAuthenticated || false,
      language: localStorageLangForPersist,
      parts: [],
      isOtpVerify: false,
    };
    window.sessionStorage.setItem(
      "coralis-tenants-storeuser",
      JSON.stringify(newSnapshot),
    );
  } catch (e) {
    console.error("Error creating initial snapshot:", e);
  }
}

const localStorageLangBeforePersist = window.localStorage.getItem(
  "coralis-language-preference",
);

persist("coralis-tenants-storeuser", userStore, {
  storage: window.sessionStorage,
  jsonify: true,
  shouldPersist: (snapshot) => snapshot?.isAuthenticated === true,
});

if (localStorageLangBeforePersist) {
  if (userStore.language !== localStorageLangBeforePersist) {
    console.log(
      "Restoring language from localStorage after persist:",
      localStorageLangBeforePersist,
      "Current:",
      userStore.language,
    );
    userStore.language = localStorageLangBeforePersist;
    try {
      const sessionData = window.sessionStorage.getItem(
        "coralis-tenants-storeuser",
      );
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        parsed.language = localStorageLangBeforePersist;
        window.sessionStorage.setItem(
          "coralis-tenants-storeuser",
          JSON.stringify(parsed),
        );
      }
    } catch (e) {
      // Ignore errors
    }
  }
} else if (userStore.language) {
  try {
    window.localStorage.setItem(
      "coralis-language-preference",
      userStore.language,
    );
    console.log("Saving initial language to localStorage:", userStore.language);
  } catch (e) {
    console.error("Failed to save initial language to localStorage:", e);
  }
}

const finalLangCheck = window.localStorage.getItem(
  "coralis-language-preference",
);
if (!finalLangCheck && userStore.language) {
  try {
    window.localStorage.setItem(
      "coralis-language-preference",
      userStore.language,
    );
    console.log(
      "Final safety save: language saved to localStorage:",
      userStore.language,
    );
  } catch (e) {
    console.error("Failed to save language to localStorage in final check:", e);
  }
}

export default userStore;
export { Role };
